import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  FlatList,
  TouchableOpacity,
  ActivityIndicator,
  RefreshControl,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { DirectionalIcon } from '../components/DirectionalIcon';
import { ScreenLayout, Header } from '../components';
import { Input, PhoneInput, LanguagePicker } from '../components/design-system';
import { TemplateBodyEditor } from '../components/WhatsAppMessageForm/components/TemplateBodyEditor';
import { TemplateHeaderAssetInput } from '../components/WhatsAppMessageForm/components/TemplateHeaderAssetInput';
import { HeaderAsset } from '../components/WhatsAppMessageForm/types/messageForm.types';
import { getCountryFromPhone, getNationalNumberFromPhone } from '../utils/phoneNumber';
import { apiService } from '../services/api';
import { useAppContext } from '../contexts/AppContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { useAppStore } from '../store/appStore';
import { colors, spacing, typography } from '../theme';
import { designTokens } from '../theme/design-system';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { defaultPhoneCountry } from '../utils/businessDefaults';

type Category = 'MARKETING' | 'UTILITY' | 'AUTHENTICATION';
type HeaderType = 'NONE' | 'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT';
type ButtonType = 'QUICK_REPLY' | 'URL' | 'PHONE_NUMBER';

interface TemplateButtonDraft {
  id: string; // local-only key for list rendering
  type: ButtonType;
  text: string;
  url?: string;
  phoneNumber?: string; // national number as typed, without the dial code
  phoneCountry?: string; // ISO country code (e.g. 'EG', 'US'), drives the dial code prefix
}

const URL_PREFIX = 'https://';

// Guards against a leading run of duplicated scheme prefixes — e.g. pasting
// "https://example.com" into a field that already has "https://" typed
// produces "https://https://example.com"; this collapses any such repeated
// leading http(s):// runs down to exactly one. Only touches the start of the
// string, so a legitimate later occurrence (e.g. "...?redirect=https://x")
// is left alone.
const sanitizeButtonUrl = (raw: string): string => {
  const value = raw.trim();
  const leadingSchemes = value.match(/^(?:https?:\/\/)+/i);
  if (!leadingSchemes) return value;
  const lastScheme = leadingSchemes[0].match(/https?:\/\//gi)!.pop()!;
  return lastScheme.toLowerCase() + value.slice(leadingSchemes[0].length);
};

const MAX_BUTTONS_TOTAL = 10;
const MAX_URL_BUTTONS = 2;
const MAX_PHONE_BUTTONS = 1;
const MAX_BUTTON_TEXT_LENGTH = 25;

const STATUS_META: Record<string, { color: string; label: string }> = {
  APPROVED: { color: '#10B981', label: 'Approved' },
  PENDING: { color: '#F59E0B', label: 'Pending review' },
  REJECTED: { color: '#EF4444', label: 'Rejected' },
  PAUSED: { color: '#F59E0B', label: 'Paused' },
  DISABLED: { color: '#EF4444', label: 'Disabled' },
};

const QUALITY_META: Record<string, { color: any; label: string }> = {
  GREEN: { color: '#10B981', label: 'High quality' },
  YELLOW: { color: '#F59E0B', label: 'Medium quality' },
  RED: { color: '#EF4444', label: 'Low quality' },
  UNKNOWN: { color: colors.textLight, label: 'Quality pending' },
};

function OptionPicker<T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: { value: T; label: string }[];
  value: T | undefined;
  onChange: (v: T) => void;
}) {
  const styles = useThemedStyles(getStyles);
  return (
    <View style={styles.pickerGroup}>
      <Text style={styles.fieldLabel}>{label}</Text>
      <View style={styles.optionRow}>
        {options.map((opt) => (
          <TouchableOpacity
            key={opt.value}
            style={[styles.optionChip, value === opt.value && styles.optionChipActive]}
            onPress={() => onChange(opt.value)}
            activeOpacity={0.7}
          >
            <Text style={[styles.optionChipText, value === opt.value && styles.optionChipTextActive]}>
              {opt.label}
            </Text>
          </TouchableOpacity>
        ))}
      </View>
    </View>
  );
}

// Extracts {{1}}, {{2}}... positions from body text, in first-seen order.
function extractBodyPositions(text: string): number[] {
  const regex = /\{\{(\d+)\}\}/g;
  const positions: number[] = [];
  let match;
  while ((match = regex.exec(text)) !== null) {
    positions.push(parseInt(match[1], 10));
  }
  return [...new Set(positions)].sort((a, b) => a - b);
}

const EDITABLE_STATUSES = new Set(['APPROVED', 'REJECTED', 'PAUSED']);

const TemplateCard: React.FC<{ template: any; onEdit: (template: any) => void }> = ({ template, onEdit }) => {
  const styles = useThemedStyles(getStyles);
  const meta = STATUS_META[template.metaStatus] || { color: colors.textLight, label: template.metaStatus ? template.metaStatus : 'Not submitted' };
  const qualityMeta = template.metaQualityScore ? QUALITY_META[template.metaQualityScore] : undefined;
  // Meta's own category can differ from the locally-requested one (e.g. auto-recategorized from UTILITY to MARKETING)
  const displayCategory = template.metaCategory || template.category;
  const categoryChanged = !!template.metaCategory && template.metaCategory !== template.category;
  const canEdit = !!template.metaTemplateId && EDITABLE_STATUSES.has(template.metaStatus);

  return (
    <View style={styles.card}>
      <View style={styles.cardBody}>
        <View style={styles.cardTitleRow}>
          <Text style={styles.cardName} numberOfLines={1}>{template.templateName}</Text>
          <View style={[styles.statusDot, { backgroundColor: meta.color }]} />
          <Text style={[styles.statusLabel, { color: meta.color }]}>{meta.label}</Text>
        </View>
        <View style={styles.cardMetaRow}>
          <Text style={styles.cardCategory}>
            {displayCategory} · {template.language}
            {categoryChanged && ' (recategorized by Meta)'}
          </Text>
          {qualityMeta && (
            <View style={styles.qualityBadge}>
              <View style={[styles.statusDot, { backgroundColor: qualityMeta.color }]} />
              <Text style={[styles.qualityLabel, { color: qualityMeta.color }]}>{qualityMeta.label}</Text>
            </View>
          )}
        </View>
        <Text style={styles.cardDetail} numberOfLines={2}>{template.templateBody}</Text>
        {template.metaStatus === 'REJECTED' && template.metaRejectionReason && (
          <Text style={styles.rejectionReason}>Reason: {template.metaRejectionReason}</Text>
        )}
      </View>
      {canEdit && (
        <TouchableOpacity style={styles.editButton} onPress={() => onEdit(template)} activeOpacity={0.7} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
          <Ionicons name="pencil-outline" size={18} color={colors.primary} />
        </TouchableOpacity>
      )}
    </View>
  );
};

export const TemplateManagementScreen: React.FC = () => {
  const styles = useThemedStyles(getStyles);
  const { navigateTo, activeTab, setActiveTab, showNotification } = useAppContext();
  const { currentSubscription, countries } = useAppStore();
  const canOverrideFooter = !!currentSubscription?.services?.wabu?.overrideFooter;

  const [mode, setMode] = useState<'list' | 'create' | 'edit'>('list');
  const [templates, setTemplates] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<any | null>(null);

  // Create form state
  const [templateName, setTemplateName] = useState('');
  const [category, setCategory] = useState<Category | undefined>(undefined);
  const [language, setLanguage] = useState('en_US');
  const [headerType, setHeaderType] = useState<HeaderType>('NONE');
  const [templateHeader, setTemplateHeader] = useState('');
  const [headerAsset, setHeaderAsset] = useState<HeaderAsset | undefined>(undefined);
  const [templateBody, setTemplateBody] = useState('');
  const DEFAULT_TEMPLATE_FOOTER = 'via DISLIX';
  const [templateFooter, setTemplateFooter] = useState(DEFAULT_TEMPLATE_FOOTER);
  const [bodyExamples, setBodyExamples] = useState<Record<number, string>>({});
  const [headerExamples, setHeaderExamples] = useState<Record<number, string>>({});
  const [buttons, setButtons] = useState<TemplateButtonDraft[]>([]);
  const [buttonErrors, setButtonErrors] = useState<Record<string, string>>({});
  // Phone-number buttons use the shared PhoneInput, whose selected dial code is only
  // readable imperatively (no onCountryChange prop) — keyed refs let buildButtonsPayload
  // pull each button's chosen country at submit time without re-rendering on every keystroke.
  const phoneButtonRefs = useRef<Record<string, { getCountryCode: () => string } | null>>({});
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const loadTemplates = useCallback(async () => {
    try {
      const res = await apiService.getWhatsAppCustomTemplates();
      if (res.statusCode === 1000) {
        setTemplates(res.data?.templates || res.data || []);
      }
    } catch (e: any) {
      showNotification('error', 'Error', e.message || 'Failed to load templates');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [showNotification]);

  const syncInFlightRef = useRef(false);

  const syncFromMeta = useCallback(async (opts?: { silent?: boolean }) => {
    if (syncInFlightRef.current) return; // avoid overlapping syncs (e.g. StrictMode double-mount, rapid taps)
    syncInFlightRef.current = true;
    try {
      const res = await apiService.syncWhatsAppTemplatesFromMeta();
      if (res.statusCode === 1000 && res.data) {
        setTemplates(res.data.templates || []);
        const { discoveredCount, updatedCount, deactivatedCount } = res.data;
        if (!opts?.silent && (discoveredCount > 0 || updatedCount > 0 || deactivatedCount > 0)) {
          const parts = [
            discoveredCount > 0 ? `${discoveredCount} discovered` : null,
            updatedCount > 0 ? `${updatedCount} updated` : null,
            deactivatedCount > 0 ? `${deactivatedCount} removed` : null,
          ].filter(Boolean);
          showNotification('success', 'Templates synced', parts.join(', ') + '.');
        }
      } else if (!opts?.silent) {
        showNotification('error', 'Sync failed', res.errors?.[0]?.message || 'Failed to sync templates from Meta');
      }
    } catch (e: any) {
      // Fall back to whatever is already stored locally rather than leaving the screen blank
      await loadTemplates();
      if (!opts?.silent) {
        showNotification('error', 'Sync failed', e.message || 'Failed to sync templates from Meta');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
      setSyncing(false);
      syncInFlightRef.current = false;
    }
  }, [showNotification, loadTemplates]);

  useEffect(() => {
    // Discover + sync from Meta on open, so approvals/rejections and templates
    // created directly on Meta show up without a manual step.
    syncFromMeta({ silent: true });
  }, [syncFromMeta]);

  const handleRefresh = () => {
    setRefreshing(true);
    syncFromMeta();
  };

  const handleManualSync = () => {
    setSyncing(true);
    syncFromMeta();
  };

  const resetForm = () => {
    setTemplateName('');
    setCategory(undefined);
    setLanguage('en_US');
    setHeaderType('NONE');
    setTemplateHeader('');
    setHeaderAsset(undefined);
    setTemplateBody('');
    setTemplateFooter(DEFAULT_TEMPLATE_FOOTER);
    setBodyExamples({});
    setHeaderExamples({});
    setButtons([]);
    setButtonErrors({});
    setErrors({});
    setEditingTemplate(null);
  };

  const handleEditPress = (template: any) => {
    setEditingTemplate(template);
    setTemplateName(template.templateName);
    setCategory((template.metaCategory || template.category) as Category);
    setLanguage(template.language || 'en_US');
    setHeaderType(template.headerAsset?.type ? (template.headerAsset.type.toUpperCase() as HeaderType) : (template.templateHeader ? 'TEXT' : 'NONE'));
    setTemplateHeader(template.templateHeader || '');
    // Meta doesn't return the original example file back on read, but the asset's
    // type/isParameterized/position metadata is preserved so the header UI reflects
    // the real template shape; a fresh example file must still be uploaded to resubmit.
    setHeaderAsset(template.headerAsset || undefined);
    setTemplateBody(template.templateBody || '');
    setTemplateFooter(template.templateFooter || DEFAULT_TEMPLATE_FOOTER);
    const examples: Record<number, string> = {};
    const headerExamplesFromTemplate: Record<number, string> = {};
    (template.parameters || []).forEach((p: any) => {
      if (p.section === 'body' && p.placeholder) examples[p.position] = p.placeholder;
      if (p.section === 'header' && p.placeholder) headerExamplesFromTemplate[p.position] = p.placeholder;
    });
    setBodyExamples(examples);
    setHeaderExamples(headerExamplesFromTemplate);
    // quickReplyItemId isn't persisted/restored here — it's a client-only webhook-matching
    // concern re-registered on next submit via registerQuickReplyItems().
    setButtons(
      (template.buttons || []).map((b: any) => {
        const id = `btn_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
        if (b.type === 'PHONE_NUMBER' && b.phoneNumber) {
          return {
            id,
            type: b.type,
            text: b.text,
            phoneCountry: getCountryFromPhone(b.phoneNumber) || defaultPhoneCountry(),
            phoneNumber: getNationalNumberFromPhone(b.phoneNumber),
          };
        }
        return {
          id,
          type: b.type,
          text: b.text,
          url: b.type === 'URL' ? b.url : undefined,
        };
      })
    );
    setButtonErrors({});
    setErrors({});
    setMode('edit');
  };

  const buttonCounts = {
    total: buttons.length,
    quickReply: buttons.filter(b => b.type === 'QUICK_REPLY').length,
    url: buttons.filter(b => b.type === 'URL').length,
    phone: buttons.filter(b => b.type === 'PHONE_NUMBER').length,
  };

  const canAddButtonType = (type: ButtonType): string | null => {
    if (buttonCounts.total >= MAX_BUTTONS_TOTAL) return `At most ${MAX_BUTTONS_TOTAL} buttons are allowed`;
    if (type === 'URL' && buttonCounts.url >= MAX_URL_BUTTONS) return `At most ${MAX_URL_BUTTONS} website buttons are allowed`;
    if (type === 'PHONE_NUMBER' && buttonCounts.phone >= MAX_PHONE_BUTTONS) return `At most ${MAX_PHONE_BUTTONS} phone number button is allowed`;
    return null;
  };

  const handleAddButton = (type: ButtonType) => {
    const blockReason = canAddButtonType(type);
    if (blockReason) {
      showNotification('error', 'Cannot add button', blockReason);
      return;
    }
    setButtons(prev => [...prev, { id: `btn_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`, type, text: '', url: type === 'URL' ? URL_PREFIX : undefined, phoneNumber: type === 'PHONE_NUMBER' ? '' : undefined, phoneCountry: type === 'PHONE_NUMBER' ? defaultPhoneCountry() : undefined }]);
  };

  const updateButton = (id: string, patch: Partial<TemplateButtonDraft>) => {
    setButtons(prev => prev.map(b => (b.id === id ? { ...b, ...patch } : b)));
  };

  const updateButtonUrl = (id: string, raw: string) => {
    updateButton(id, { url: sanitizeButtonUrl(raw) });
  };

  const removeButton = (id: string) => {
    setButtons(prev => prev.filter(b => b.id !== id));
    setButtonErrors(prev => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const validateButtons = (): boolean => {
    const errs: Record<string, string> = {};
    for (const b of buttons) {
      if (!b.text.trim()) {
        errs[b.id] = 'Button text is required';
      } else if (b.text.length > MAX_BUTTON_TEXT_LENGTH) {
        errs[b.id] = `Button text must be at most ${MAX_BUTTON_TEXT_LENGTH} characters`;
      } else if (b.type === 'URL' && (!b.url || !/^https?:\/\/.+\..+/i.test(b.url))) {
        errs[b.id] = 'Enter a valid URL starting with http:// or https://';
      } else if (b.type === 'PHONE_NUMBER' && (!b.phoneNumber || !/^[0-9]{6,15}$/.test(b.phoneNumber))) {
        errs[b.id] = 'Enter a valid phone number';
      }
    }
    setButtonErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // Every QUICK_REPLY button also becomes a standalone, reusable Quick Reply item
  // elsewhere in the app. This MUST run and produce item IDs BEFORE the template is
  // submitted to Meta: each button's created item _id is sent to Meta as that
  // button's `payload`, which WhatsApp echoes back verbatim when the recipient taps
  // it — that's the only way whatsapp-webhook.service.ts can recognize "this button
  // tap should trigger quick-reply item X" (it matches on an `item_<id>` prefix).
  // Without a payload, Meta echoes the button's display TEXT instead, and a tap does
  // nothing from Sendifier's side even though WhatsApp delivered it fine. Returns a
  // map of button id -> created quick-reply item id so buildButtonsPayload can attach
  // it; a button whose item creation failed is submitted without a payload (falls
  // back to Meta's default text-echo behavior, same as before this fix) rather than
  // blocking the whole template submission.
  const registerQuickReplyItems = async (): Promise<Record<string, string>> => {
    const quickReplies = buttons.filter(b => b.type === 'QUICK_REPLY' && b.text.trim());
    const results = await Promise.all(
      quickReplies.map(async b => {
        try {
          const res = await apiService.createQuickReplyItem({
            displayText: b.text.trim(),
            type: 'fixed_message',
            config: { fixedMessage: { messageType: 'text', text: b.text.trim() } },
            isActive: true,
          });
          const itemId = res?.data?._id || res?.data?.id;
          return itemId ? [b.id, itemId] as const : null;
        } catch {
          // Non-fatal: the button still gets submitted to Meta even if the
          // reusable Quick Reply item couldn't be created — just without a payload.
          return null;
        }
      })
    );
    return Object.fromEntries(results.filter((r): r is [string, string] => r !== null));
  };

  const validate = (): boolean => {
    const e: Record<string, string> = {};
    if (!templateName.trim()) {
      e.templateName = 'Template name is required';
    } else if (!/^[a-z0-9_]+$/.test(templateName.trim())) {
      e.templateName = 'Use lowercase letters, numbers, and underscores only';
    }
    if (!category) e.category = 'Category is required';
    if (!language.trim()) e.language = 'Language is required';
    if (!templateBody.trim()) {
      e.templateBody = 'Body text is required';
    } else if (templateBody.length > 1024) {
      e.templateBody = 'Body text must be at most 1024 characters';
    }
    if (headerType === 'TEXT' && templateHeader.length > 60) {
      e.templateHeader = 'Header text must be at most 60 characters';
    }
    if ((headerType === 'IMAGE' || headerType === 'VIDEO' || headerType === 'DOCUMENT') && !headerAsset?.url) {
      e.headerAsset = `An example ${headerType.toLowerCase()} file is required — Meta rejects media headers submitted without one`;
    }
    if (canOverrideFooter && templateFooter.length > 60) {
      e.templateFooter = 'Footer text must be at most 60 characters';
    }
    const bodyPositions = extractBodyPositions(templateBody);
    const missingExamples = bodyPositions.filter(pos => !bodyExamples[pos]?.trim());
    if (missingExamples.length > 0) {
      e.bodyExamples = `Provide an example value for ${missingExamples.map(p => `{{${p}}}`).join(', ')}`;
    }
    if (headerType === 'TEXT') {
      const headerPositions = extractBodyPositions(templateHeader);
      const missingHeaderExamples = headerPositions.filter(pos => !headerExamples[pos]?.trim());
      if (missingHeaderExamples.length > 0) {
        e.headerExamples = `Provide an example value for ${missingHeaderExamples.map(p => `{{${p}}}`).join(', ')}`;
      }
      // Meta numbers {{n}} placeholders uniquely across the WHOLE template — a header
      // {{1}} and a body {{1}} are not independent, so reusing a number in both is
      // rejected on submit (and would collide when send-time input fields are built).
      const overlapping = headerPositions.filter(pos => bodyPositions.includes(pos));
      if (overlapping.length > 0) {
        e.templateHeader = `{{${overlapping.join('}}, {{')}}} is already used in the body — each variable number must be unique across the whole template`;
      }
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const buildButtonsPayload = (quickReplyItemIdByButtonId: Record<string, string> = {}) =>
    buttons.map(b => {
      let fullPhoneNumber: string | undefined;
      if (b.type === 'PHONE_NUMBER' && b.phoneNumber?.trim()) {
        const countryCode = phoneButtonRefs.current[b.id]?.getCountryCode() || (countries.find(c => c.code === (b.phoneCountry || defaultPhoneCountry()))?.dialCode || '').replace('+', '');
        fullPhoneNumber = `+${countryCode}${b.phoneNumber.trim()}`;
      }
      return {
        type: b.type,
        text: b.text.trim(),
        url: b.type === 'URL' ? b.url?.trim() : undefined,
        phoneNumber: fullPhoneNumber,
        quickReplyItemId: b.type === 'QUICK_REPLY' ? quickReplyItemIdByButtonId[b.id] : undefined,
      };
    });

  const handleSubmit = async () => {
    if (!validate()) return;
    if (!validateButtons()) return;
    try {
      setSaving(true);
      const bodyPositions = extractBodyPositions(templateBody);
      // Header {{n}} placeholders (typed directly into a TEXT header) must be declared too —
      // otherwise the send flow never learns the header needs a value at all and it reaches
      // recipients as a literal, unfilled "{{1}}".
      const headerPositions = headerType === 'TEXT' ? extractBodyPositions(templateHeader) : [];
      const parameters = [
        ...headerPositions.map(position => ({
          position,
          section: 'header' as const,
          placeholder: headerExamples[position],
          isRequired: true,
        })),
        ...bodyPositions.map(position => ({
          position,
          section: 'body' as const,
          placeholder: bodyExamples[position],
          isRequired: true,
        })),
      ];
      // Quick-reply items must exist BEFORE building the buttons payload, so each
      // QUICK_REPLY button can carry its item's id as Meta's button `payload` — see
      // registerQuickReplyItems for why a tap does nothing without this.
      const quickReplyItemIdByButtonId = await registerQuickReplyItems();
      const buttonsPayload = buttons.length > 0 ? buildButtonsPayload(quickReplyItemIdByButtonId) : undefined;

      if (mode === 'edit' && editingTemplate) {
        const res = await apiService.editWhatsAppTemplateOnMeta(editingTemplate._id, {
          templateBody: templateBody.trim(),
          templateHeader: headerType === 'TEXT' && templateHeader.trim() ? templateHeader.trim() : undefined,
          headerType: headerType === 'NONE' ? undefined : headerType,
          headerMediaUrl: headerAsset?.url,
          templateFooter: canOverrideFooter ? (templateFooter.trim() || DEFAULT_TEMPLATE_FOOTER) : DEFAULT_TEMPLATE_FOOTER,
          language: language.trim(),
          category: category!,
          buttons: buttonsPayload,
          parameters,
        });

        if (res.statusCode === 1000) {
          showNotification('success', 'Template updated', `"${editingTemplate.templateName}" was resubmitted to Meta for review.`);
          resetForm();
          setMode('list');
          loadTemplates();
        } else {
          showNotification('error', 'Edit failed', res.errors?.[0]?.message || 'Failed to edit template');
        }
        return;
      }

      const res = await apiService.submitWhatsAppTemplateToMeta({
        templateName: templateName.trim(),
        templateBody: templateBody.trim(),
        templateHeader: headerType === 'TEXT' && templateHeader.trim() ? templateHeader.trim() : undefined,
        headerType: headerType === 'NONE' ? undefined : headerType,
        headerMediaUrl: headerAsset?.url,
        templateFooter: canOverrideFooter ? (templateFooter.trim() || DEFAULT_TEMPLATE_FOOTER) : DEFAULT_TEMPLATE_FOOTER,
        language: language.trim(),
        category: category!,
        buttons: buttonsPayload,
        parameters,
      });

      if (res.statusCode === 1000) {
        showNotification('success', 'Template submitted', `"${templateName}" was submitted to Meta for approval. You'll be notified once it's reviewed.`);
        resetForm();
        setMode('list');
        loadTemplates();
      } else {
        showNotification('error', 'Submission failed', res.errors?.[0]?.message || 'Failed to submit template');
      }
    } catch (e: any) {
      showNotification('error', 'Error', e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);

  const bodyPositions = extractBodyPositions(templateBody);
  const headerPositions = headerType === 'TEXT' ? extractBodyPositions(templateHeader) : [];

  return (
    <ScreenLayout
      header={
        <Header
          showNotifications={true}
          showMessages={true}
          onNotificationPress={() => navigateTo('notifications')}
          onMessagePress={() => navigateTo('chat')}
          showNavigation={Platform.OS === 'web'}
          activeTab={activeTab}
          onTabPress={handleTabPress}
        />
      }
      showBottomTabs={Platform.OS !== 'web'}
      onLeftPaneClose={() => {}}
      leftPaneContent="settings"
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      onRightPaneClose={() => {}}
    >
      <View style={styles.container}>
        <View style={styles.header}>
          <View style={styles.headerContent}>
            <View style={styles.headerLeft}>
              <TouchableOpacity
                style={styles.backButton}
                onPress={() => {
                  if (mode !== 'list') {
                    resetForm();
                    setMode('list');
                  } else if (Platform.OS === 'web') {
                    window.history.back();
                  } else {
                    navigateTo('settings');
                  }
                }}
                activeOpacity={0.7}
              >
                <DirectionalIcon name="chevron-back" size={20} color={colors.textDark} />
              </TouchableOpacity>
              <View>
                <Text style={styles.headerTitle}>
                  {mode === 'list' ? 'Message Templates' : mode === 'edit' ? 'Edit Template' : 'New Template'}
                </Text>
                <Text style={styles.headerSubtitle}>
                  {mode === 'list'
                    ? 'Submit and track WhatsApp template approvals'
                    : mode === 'edit'
                    ? 'Changes will be resubmitted to Meta for review'
                    : 'Submitted for Meta review'}
                </Text>
              </View>
            </View>

            {mode === 'list' ? (
              <View style={styles.headerActions}>
                <TouchableOpacity
                  style={[styles.syncButton, syncing && styles.saveButtonDisabled]}
                  onPress={handleManualSync}
                  disabled={syncing}
                  activeOpacity={0.8}
                >
                  {syncing ? (
                    <ActivityIndicator size="small" color={colors.primary} />
                  ) : (
                    <Ionicons name="sync" size={16} color={colors.primary} />
                  )}
                </TouchableOpacity>
                <TouchableOpacity style={styles.saveButton} onPress={() => setMode('create')} activeOpacity={0.8}>
                  <Text style={styles.saveButtonText}>New</Text>
                </TouchableOpacity>
              </View>
            ) : (
              <TouchableOpacity
                style={[styles.saveButton, saving && styles.saveButtonDisabled]}
                onPress={handleSubmit}
                disabled={saving}
                activeOpacity={0.8}
              >
                {saving ? <ActivityIndicator size="small" color={colors.white} /> : <Text style={styles.saveButtonText}>Submit</Text>}
              </TouchableOpacity>
            )}
          </View>
        </View>

        {mode === 'list' ? (
          loading ? (
            <View style={styles.centerContent}>
              <ActivityIndicator size="large" color={colors.primary} />
            </View>
          ) : (
            <FlatList
              data={templates}
              keyExtractor={(item) => item._id}
              contentContainerStyle={styles.content}
              refreshControl={<RefreshControl refreshing={refreshing} onRefresh={handleRefresh} />}
              ListEmptyComponent={
                <View style={styles.centerContent}>
                  <Ionicons name="document-text-outline" size={48} color={colors.textLight} />
                  <Text style={styles.emptyText}>No templates yet</Text>
                </View>
              }
              renderItem={({ item }) => <TemplateCard template={item} onEdit={handleEditPress} />}
            />
          )
        ) : (
          <ScrollView contentContainerStyle={styles.content} showsVerticalScrollIndicator={false}>
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Basic Info</Text>
              <Input
                label="Template Name *"
                value={templateName}
                onChangeText={(t) => setTemplateName(t.toLowerCase().replace(/[^a-z0-9_]/g, '_'))}
                placeholder="order_confirmation"
                error={errors.templateName}
                autoCapitalize="none"
                editable={mode !== 'edit'}
                helperText={mode === 'edit' ? 'Name cannot be changed once submitted' : undefined}
              />
              <LanguagePicker label="Language *" value={language} onChange={setLanguage} error={errors.language} />
              {errors.category && <Text style={styles.fieldError}>{errors.category}</Text>}
              {mode === 'edit' && editingTemplate?.metaStatus === 'APPROVED' ? (
                <Text style={styles.fieldHelper}>Category: {category} (cannot be changed on an approved template)</Text>
              ) : (
                <OptionPicker<Category>
                  label="Category *"
                  value={category}
                  onChange={setCategory}
                  options={[
                    { value: 'UTILITY', label: 'Utility' },
                    { value: 'MARKETING', label: 'Marketing' },
                    { value: 'AUTHENTICATION', label: 'Authentication' },
                  ]}
                />
              )}
            </View>

            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Header (optional)</Text>
              <OptionPicker<HeaderType>
                label="Header Type"
                value={headerType}
                onChange={(t) => { if (t !== headerType) { setHeaderType(t); setHeaderAsset(undefined); } }}
                options={[
                  { value: 'NONE', label: 'None' },
                  { value: 'TEXT', label: 'Text' },
                  { value: 'IMAGE', label: 'Image' },
                  { value: 'VIDEO', label: 'Video' },
                  { value: 'DOCUMENT', label: 'Document' },
                ]}
              />
              {headerType === 'TEXT' && (
                <>
                  <Input
                    label="Header Text (max 60 chars)"
                    value={templateHeader}
                    onChangeText={setTemplateHeader}
                    placeholder="Order confirmed"
                    error={errors.templateHeader}
                    maxLength={60}
                  />
                  {headerPositions.length > 0 && (
                    <View style={styles.exampleGroup}>
                      <Text style={styles.fieldLabel}>Example values (required by Meta for review)</Text>
                      {errors.headerExamples && <Text style={styles.fieldError}>{errors.headerExamples}</Text>}
                      {headerPositions.map(pos => (
                        <Input
                          key={pos}
                          label={`Example for {{${pos}}}`}
                          value={headerExamples[pos] || ''}
                          onChangeText={(v) => setHeaderExamples(prev => ({ ...prev, [pos]: v }))}
                          placeholder="e.g. John"
                        />
                      ))}
                    </View>
                  )}
                </>
              )}
              {(headerType === 'IMAGE' || headerType === 'VIDEO' || headerType === 'DOCUMENT') && (
                <>
                  <Text style={styles.fieldHelper}>
                    Meta requires an example {headerType.toLowerCase()} file to review this template. Recipients will still need to receive their own file when the template is sent.
                  </Text>
                  <TemplateHeaderAssetInput
                    headerAsset={headerAsset}
                    onAssetChange={setHeaderAsset}
                    headerType={headerType.toLowerCase() as 'image' | 'video' | 'document'}
                  />
                  {errors.headerAsset && <Text style={styles.fieldError}>{errors.headerAsset}</Text>}
                </>
              )}
            </View>

            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Body *</Text>
              <TemplateBodyEditor
                label="Body Text — use {{1}}, {{2}}... for variables"
                value={templateBody}
                onChangeText={setTemplateBody}
                placeholder="Hi {{1}}, your order will arrive by {{2}}."
                error={errors.templateBody}
                maxLength={1024}
              />
              {bodyPositions.length > 0 && (
                <View style={styles.exampleGroup}>
                  <Text style={styles.fieldLabel}>Example values (required by Meta for review)</Text>
                  {errors.bodyExamples && <Text style={styles.fieldError}>{errors.bodyExamples}</Text>}
                  {bodyPositions.map(pos => (
                    <Input
                      key={pos}
                      label={`Example for {{${pos}}}`}
                      value={bodyExamples[pos] || ''}
                      onChangeText={(v) => setBodyExamples(prev => ({ ...prev, [pos]: v }))}
                      placeholder="e.g. John"
                    />
                  ))}
                </View>
              )}
            </View>

            {canOverrideFooter && (
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>Footer (optional)</Text>
                <Input
                  label="Footer Text (max 60 chars, no variables)"
                  value={templateFooter}
                  onChangeText={setTemplateFooter}
                  placeholder="Reply STOP to unsubscribe"
                  error={errors.templateFooter}
                  maxLength={60}
                />
              </View>
            )}

            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Buttons (optional)</Text>
              <Text style={styles.fieldHelper}>
                Up to {MAX_BUTTONS_TOTAL} buttons: max {MAX_URL_BUTTONS} website links, max {MAX_PHONE_BUTTONS} phone call. Quick reply buttons are also saved as reusable Quick Replies.
              </Text>

              {buttons.map((b) => (
                <View key={b.id} style={styles.buttonRow}>
                  <View style={styles.buttonRowHeader}>
                    <Text style={styles.buttonTypeLabel}>
                      {b.type === 'QUICK_REPLY' ? 'Quick Reply' : b.type === 'URL' ? 'Visit Website' : 'Call Phone Number'}
                    </Text>
                    <TouchableOpacity onPress={() => removeButton(b.id)} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
                      <Ionicons name="trash-outline" size={18} color={colors.error} />
                    </TouchableOpacity>
                  </View>
                  <Input
                    label="Button text (max 25 chars) — customize as needed"
                    value={b.text}
                    onChangeText={(t) => updateButton(b.id, { text: t })}
                    placeholder={b.type === 'URL' ? 'Visit Website' : b.type === 'PHONE_NUMBER' ? 'Call Us' : 'Yes, please'}
                    maxLength={MAX_BUTTON_TEXT_LENGTH}
                    error={buttonErrors[b.id]}
                  />
                  {b.type === 'URL' && (
                    <Input
                      label="URL"
                      value={b.url || ''}
                      onChangeText={(t) => updateButtonUrl(b.id, t)}
                      onBlur={() => updateButtonUrl(b.id, b.url || URL_PREFIX)}
                      placeholder="https://example.com"
                      autoCapitalize="none"
                      keyboardType="url"
                      error={buttonErrors[b.id]?.includes('URL') ? buttonErrors[b.id] : undefined}
                    />
                  )}
                  {b.type === 'PHONE_NUMBER' && (
                    <PhoneInput
                      ref={(r) => { phoneButtonRefs.current[b.id] = r; }}
                      label="Phone number"
                      value={b.phoneNumber || ''}
                      initialCountry={b.phoneCountry || defaultPhoneCountry()}
                      onChangeText={(t) => updateButton(b.id, { phoneNumber: t.replace(/\D/g, '') })}
                      onChange={(t) => updateButton(b.id, { phoneNumber: t.replace(/\D/g, '') })}
                      placeholder="Phone number"
                      error={buttonErrors[b.id]?.includes('phone') ? buttonErrors[b.id] : undefined}
                    />
                  )}
                </View>
              ))}

              <View style={styles.addButtonRow}>
                <TouchableOpacity
                  style={styles.addButtonChip}
                  onPress={() => handleAddButton('QUICK_REPLY')}
                  disabled={!!canAddButtonType('QUICK_REPLY')}
                  activeOpacity={0.7}
                >
                  <Ionicons name="chatbubble-outline" size={14} color={colors.primary} />
                  <Text style={styles.addButtonChipText}>Quick Reply</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.addButtonChip, !!canAddButtonType('URL') && styles.addButtonChipDisabled]}
                  onPress={() => handleAddButton('URL')}
                  disabled={!!canAddButtonType('URL')}
                  activeOpacity={0.7}
                >
                  <Ionicons name="link-outline" size={14} color={colors.primary} />
                  <Text style={styles.addButtonChipText}>Visit Website</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.addButtonChip, !!canAddButtonType('PHONE_NUMBER') && styles.addButtonChipDisabled]}
                  onPress={() => handleAddButton('PHONE_NUMBER')}
                  disabled={!!canAddButtonType('PHONE_NUMBER')}
                  activeOpacity={0.7}
                >
                  <Ionicons name="call-outline" size={14} color={colors.primary} />
                  <Text style={styles.addButtonChipText}>Call Phone</Text>
                </TouchableOpacity>
              </View>
            </View>
          </ScrollView>
        )}
      </View>
    </ScreenLayout>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.lightGray,
  },
  header: {
    backgroundColor: colors.background.surface,
    paddingVertical: spacing.lg,
    paddingHorizontal: spacing.lg,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    ...designTokens.shadows.sm,
  },
  headerContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  headerLeft: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  backButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    borderWidth: 1.5,
    borderColor: colors.border,
    backgroundColor: colors.background.surface,
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: typography.fontSize.xl,
    fontWeight: '700',
    color: colors.textDark,
    fontFamily: typography.fontFamily.bold,
  },
  headerSubtitle: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    fontFamily: typography.fontFamily.regular,
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  syncButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    borderWidth: 1.5,
    borderColor: colors.border,
    backgroundColor: colors.background.surface,
    justifyContent: 'center',
    alignItems: 'center',
  },
  saveButton: {
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
    borderRadius: 20,
    backgroundColor: colors.primary,
    justifyContent: 'center',
    alignItems: 'center',
    minWidth: 64,
    ...designTokens.shadows.sm,
  },
  saveButtonDisabled: { opacity: 0.6 },
  saveButtonText: {
    color: colors.white,
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.semibold,
  },
  content: {
    padding: spacing.md,
    gap: spacing.md,
    paddingBottom: Platform.OS !== 'web' ? 100 : spacing.lg,
  },
  centerContent: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: spacing.xxl,
    gap: spacing.sm,
  },
  emptyText: {
    fontSize: typography.fontSize.base,
    color: colors.textLight,
    fontFamily: typography.fontFamily.regular,
  },
  section: {
    backgroundColor: colors.background.surface,
    borderRadius: designTokens.borderRadius.lg,
    padding: spacing.md,
    gap: spacing.sm,
    marginBottom: spacing.md,
    ...designTokens.shadows.sm,
  },
  sectionTitle: {
    fontSize: typography.fontSize.sm,
    fontWeight: '600',
    color: colors.textLight,
    fontFamily: typography.fontFamily.semibold,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: spacing.xs,
  },
  fieldLabel: {
    fontSize: typography.fontSize.sm,
    color: colors.textDark,
    fontFamily: typography.fontFamily.medium,
    marginBottom: spacing.xs,
  },
  fieldError: {
    fontSize: typography.fontSize.xs,
    color: colors.error,
    fontFamily: typography.fontFamily.regular,
  },
  fieldHelper: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    fontFamily: typography.fontFamily.regular,
  },
  pickerGroup: { gap: spacing.xs },
  optionRow: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs },
  optionChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: 20,
    borderWidth: 1.5,
    borderColor: colors.border,
    backgroundColor: colors.background.surface,
  },
  optionChipActive: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  optionChipText: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    fontFamily: typography.fontFamily.medium,
  },
  optionChipTextActive: { color: colors.white },
  exampleGroup: { gap: spacing.xs, marginTop: spacing.xs },
  card: {
    flexDirection: 'row',
    backgroundColor: colors.background.surface,
    borderRadius: designTokens.borderRadius.lg,
    padding: spacing.md,
    marginBottom: spacing.sm,
    ...designTokens.shadows.sm,
  },
  cardBody: { flex: 1, gap: 2 },
  editButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: spacing.xs,
  },
  cardTitleRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs },
  cardName: {
    flex: 1,
    fontSize: typography.fontSize.base,
    fontWeight: '600',
    color: colors.textDark,
    fontFamily: typography.fontFamily.semibold,
  },
  statusDot: { width: 8, height: 8, borderRadius: 4 },
  statusLabel: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.medium,
  },
  cardMetaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: spacing.xs,
  },
  cardCategory: {
    fontSize: typography.fontSize.xs,
    color: colors.textLight,
    fontFamily: typography.fontFamily.regular,
  },
  qualityBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  qualityLabel: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.medium,
  },
  cardDetail: {
    fontSize: typography.fontSize.sm,
    color: colors.textDark,
    fontFamily: typography.fontFamily.regular,
  },
  rejectionReason: {
    fontSize: typography.fontSize.xs,
    color: colors.error,
    fontFamily: typography.fontFamily.regular,
    marginTop: 2,
  },
  buttonRow: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: designTokens.borderRadius.md,
    padding: spacing.sm,
    gap: spacing.xs,
    marginTop: spacing.sm,
  },
  buttonRowHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  buttonTypeLabel: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.semibold,
    color: colors.textDark,
  },
  addButtonRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.xs,
    marginTop: spacing.sm,
  },
  addButtonChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: 20,
    borderWidth: 1.5,
    borderColor: colors.primary,
    backgroundColor: colors.background.surface,
  },
  addButtonChipDisabled: {
    opacity: 0.4,
  },
  addButtonChipText: {
    fontSize: typography.fontSize.sm,
    color: colors.primary,
    fontFamily: typography.fontFamily.medium,
  },
});
